// Preset detail — Overview / Live state / Run history / Configuration
// (SM-538 / SM-546).
//
// This page used to be the edit form and nothing else, which meant a
// preset's identity, its current run, its state and its history had no
// home at all: an operator had to reconstruct "what is this bot doing"
// from the Runs list by matching ids. The editor is now one tab of
// four.
//
// Vocabulary is strict: a PRESET is the reusable configuration, a RUN
// is one execution attempt. The two words are never swapped, and
// "bot" appears nowhere as a label.
//
// Tab and filters live in the URL, so a refresh or a link an operator
// pastes to a colleague lands on the same view.
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { invalidateAffected } from "@/lib/routeRefresh";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Skeleton } from "@/components/ui/Skeleton";
import { PresetEditor } from "@/components/PresetEditor";
import { LifecycleActions } from "@/components/runs/LifecycleActions";
import { NameSourceBadge, TechnicalDetails } from "@/components/runs/PresetIdentity";
import { RunHistoryTable } from "@/components/runs/RunHistoryTable";
import { RunHistoryFilters, filterSummary } from "@/components/runs/RunHistoryFilters";
import {
  EMPTY_FILTERS,
  isModeFilter,
  isStatusGroup,
  statusesParam,
  type ModeFilter,
  type RunHistoryFilterState,
  type StatusGroup,
} from "@/lib/runHistory";
import { ModeBadge, RunStateTabs } from "@/components/runs/RunStateTabs";
import { isRunStateSection, runStateIsLive } from "@/lib/runHistory";
import { useI18n } from "@/i18n/I18nProvider";
import { strategyNameLabel } from "@/i18n/strategyCopy";
import { APIError } from "@/lib/api";
import { useStartRunPreflight, type StartRequest } from "@/components/runs/StartRunPreflight";
import { debitCurrency, effectiveTradingMode, payoutCurrency } from "@/lib/tradingMode";
import {
  fetchPreset,
  fetchRun,
  fetchRunHistory,
  keys,
  setPresetAutoStart,
  startRun,
  updatePreset,
} from "@/lib/queries";
import type { MMPreset, MMRun, MMRunStateSection } from "@/lib/types";

type PresetTab = "overview" | "state" | "history" | "config";

const PRESET_TABS: readonly PresetTab[] = ["overview", "state", "history", "config"] as const;

function isPresetTab(value: unknown): value is PresetTab {
  return PRESET_TABS.includes(value as PresetTab);
}

// Search schema. Every field is validated on the way in, so a
// hand-edited or stale URL degrades to the default view instead of
// rendering an undefined tab.
// Every field is OPTIONAL in the type so an existing
// <Link to="/presets/$id"> without search still type-checks; a required
// key would break every link into this page. validateSearch normalizes,
// so the component reads concrete values via the defaults below.
interface PresetSearch {
  tab?: PresetTab;
  section?: MMRunStateSection;
  status?: StatusGroup;
  mode?: ModeFilter;
  runId?: string;
  from?: string;
  to?: string;
  page?: number;
}

export const Route = createFileRoute("/_app/presets/$id")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.presetDetail),
  validateSearch: (search: Record<string, unknown>): PresetSearch => ({
    tab: isPresetTab(search.tab) ? search.tab : "overview",
    section: isRunStateSection(search.section) ? search.section : "orders",
    status: isStatusGroup(search.status) ? search.status : "all",
    mode: isModeFilter(search.mode) ? search.mode : "all",
    runId: typeof search.runId === "string" && search.runId ? search.runId : undefined,
    from: typeof search.from === "string" && search.from ? search.from : undefined,
    to: typeof search.to === "string" && search.to ? search.to : undefined,
    page: typeof search.page === "number" && search.page > 0 ? Math.floor(search.page) : 0,
  }),
  component: PresetDetailPage,
});

const HISTORY_PAGE_SIZE = 25;

function PresetDetailPage() {
  const { t, formatDateTime } = useI18n();
  const { id } = Route.useParams();
  const rawSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();

  // One normalization point: the rest of the component reads concrete
  // values, so a hand-edited URL cannot leave a tab undefined halfway
  // down the render.
  const search = {
    tab: rawSearch.tab ?? "overview",
    section: rawSearch.section ?? "orders",
    status: rawSearch.status ?? "all",
    mode: rawSearch.mode ?? "all",
    runId: rawSearch.runId,
    from: rawSearch.from,
    to: rawSearch.to,
    page: rawSearch.page ?? 0,
  } satisfies Required<Pick<PresetSearch, "tab" | "section" | "status" | "mode" | "page">> & PresetSearch;

  const presetQ = useQuery({ queryKey: keys.preset(id), queryFn: () => fetchPreset(id) });
  const preset = presetQ.data;
  const currentRunID = preset?.runs?.current_run_id;
  const latestTerminalRunID = preset?.runs?.latest_terminal_run_id;

  // The run whose state and lifecycle this page projects: the current
  // run when the preset is executing or paused, otherwise the latest
  // terminal attempt — so an idle preset still shows what it last did
  // rather than an empty page.
  const projectedRunID = currentRunID || latestTerminalRunID;
  const runQ = useQuery({
    queryKey: keys.run(projectedRunID ?? ""),
    queryFn: () => fetchRun(projectedRunID as string),
    enabled: !!projectedRunID,
    refetchInterval: (q) => (q.state.data?.is_active ? 3_000 : false),
  });
  const run = runQ.data;

  const filters: RunHistoryFilterState = {
    status: search.status,
    mode: search.mode,
    runID: search.runId ?? "",
    from: search.from ?? "",
    to: search.to ?? "",
  };

  const historyQ = useQuery({
    queryKey: keys.presetRuns({
      preset_id: id,
      statuses: statusesParam(search.status),
      trading_mode: search.mode === "all" ? undefined : search.mode,
      run_id: search.runId,
      from: search.from,
      to: search.to,
      limit: HISTORY_PAGE_SIZE,
      offset: search.page * HISTORY_PAGE_SIZE,
    }),
    queryFn: () =>
      fetchRunHistory({
        preset_id: id,
        statuses: statusesParam(search.status),
        trading_mode: search.mode === "all" ? undefined : search.mode,
        run_id: search.runId,
        from: search.from,
        to: search.to,
        limit: HISTORY_PAGE_SIZE,
        offset: search.page * HISTORY_PAGE_SIZE,
      }),
    // placeholderData keeps the previous page rendered while the next
    // one loads, so paging does not blank the table under the cursor.
    placeholderData: (prev) => prev,
    enabled: search.tab === "history",
  });

  const [saveErr, setSaveErr] = React.useState<string | null>(null);
  const saveMut = useMutation({
    mutationFn: (body: Parameters<typeof updatePreset>[1]) => updatePreset(id, body),
    onSuccess: (updated) => {
      invalidateAffected(qc, "presets");
      qc.setQueryData(keys.preset(id), updated);
      setSaveErr(null);
    },
    onError: (e) => setSaveErr(e instanceof APIError ? e.message : t("preset.error.update")),
  });

  const autoStartMut = useMutation({
    mutationFn: (enabled: boolean) => setPresetAutoStart(id, enabled),
    onSuccess: (updated) => {
      qc.setQueryData(keys.preset(id), updated);
      invalidateAffected(qc, "presets");
    },
  });

  const startMut = useMutation({
    mutationFn: (req: StartRequest) =>
      startRun({ preset_id: id, expected_funding_plan: req.expectedFundingPlan }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: keys.preset(id) });
      invalidateAffected(qc, "runs");
      navigate({ to: "/runs/$id", params: { id: created.run_id } });
    },
  });
  // Same preflight as the Presets list (SM-526 / SM-531): the GAME gate
  // as a banner, then the server's per-currency funding review. This
  // page used to start with neither.
  const preflight = useStartRunPreflight((req) => startMut.mutate(req));
  const starting = startMut.isPending || preflight.pricingId === id;

  function setSearch(patch: Partial<PresetSearch>) {
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  function startFresh() {
    if (!preset) return;
    void preflight.begin(preset);
  }

  const mode = preset ? effectiveTradingMode(preset.config) : "CASH";
  const tabLabels: Record<PresetTab, string> = {
    overview: t("presetDetail.tab.overview"),
    state: t("presetDetail.tab.state"),
    history: t("presetDetail.tab.history"),
    config: t("presetDetail.tab.config"),
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-2xs uppercase tracking-widest t-mute">
          <Link to="/presets" className="hover:t-emph">
            {t("presets.title")}
          </Link>
          {" / "}
          <span className="text-t-1">{preset?.name ?? "…"}</span>
        </div>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* Name, mode, auto-start and lifecycle status — the four
                things an operator should read before any id. */}
            <h1 className="page-title flex flex-wrap items-center gap-2">
              <span className="truncate">{preset?.name ?? t("common.loading")}</span>
              {preset && <ModeBadge mode={mode} />}
              {run && <StatusBadge status={run.status} />}
              {preset?.auto_start_enabled && (
                <Badge tone="accent" title={t("autoStart.willStartHint")}>
                  {t("autoStart.willStart")}
                </Badge>
              )}
            </h1>
            {preset?.description && <p className="page-subtitle">{preset.description}</p>}
            <TechnicalDetails presetID={preset?.preset_id} runID={run?.run_id} />
          </div>

          <div className="shrink-0">
            {run ? (
              <LifecycleActions
                run={run}
                onStartFresh={startFresh}
                startingFresh={starting}
              />
            ) : (
              preset && (
                <Button variant="primary" onClick={startFresh} loading={starting}>
                  {t("lifecycle.startRun")}
                </Button>
              )
            )}
          </div>
        </div>
      </header>
      <DataFreshness query={presetQ} relatedQueries={[historyQ, runQ]} />

      <ErrorBanner error={startMut.error} fallbackTitle={t("presets.startError")} />
      <ErrorBanner error={autoStartMut.error} fallbackTitle={t("autoStart.error")} />
      {preflight.blockedBanner}
      {preflight.dialog}

      <div className="flex flex-wrap gap-2" role="tablist">
        {PRESET_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={search.tab === tab}
            className={`page-tab ${search.tab === tab ? "is-active" : ""}`}
            onClick={() => setSearch({ tab })}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {presetQ.isLoading && <Skeleton lines={8} />}
      <ErrorBanner error={presetQ.error} fallbackTitle={t("presetDetail.error.load")} />

      {preset && search.tab === "overview" && (
        <OverviewTab
          preset={preset}
          run={run}
          runLoading={runQ.isLoading}
          onToggleAutoStart={(enabled) => autoStartMut.mutate(enabled)}
          togglingAutoStart={autoStartMut.isPending}
        />
      )}

      {preset && search.tab === "state" && (
        <SectionCard
          title={
            run ? (
              <>
                {t("presetDetail.liveStateTitle")}
                <Link
                  to="/runs/$id"
                  params={{ id: run.run_id }}
                  className="ml-2 text-xs t-emph hover:underline"
                >
                  {t("presetDetail.openRun")}
                </Link>
              </>
            ) : (
              t("presetDetail.liveStateTitle")
            )
          }
        >
          {!projectedRunID ? (
            <p className="t-mute text-sm">{t("presetDetail.noRunsYet")}</p>
          ) : (
            <>
              {/* When the preset is idle this is the LATEST TERMINAL
                  run's frozen snapshot, and the state header says so —
                  historical data must never look live. */}
              {!currentRunID && (
                <p className="mb-3 text-xs t-mute">
                  {t("presetDetail.showingLatestTerminal", {
                    time: run?.stop_at ? formatDateTime(run.stop_at) : "—",
                  })}
                </p>
              )}
              <RunStateTabs
                runID={projectedRunID}
                live={runStateIsLive(run)}
                section={search.section}
                onSectionChange={(section) => setSearch({ section })}
              />
            </>
          )}
        </SectionCard>
      )}

      {preset && search.tab === "history" && (
        <SectionCard title={t("presetDetail.tab.history")}>
          <div className="space-y-4">
            <RunHistoryFilters
              value={filters}
              onChange={(next) =>
                setSearch({
                  status: next.status,
                  mode: next.mode,
                  runId: next.runID || undefined,
                  from: next.from || undefined,
                  to: next.to || undefined,
                  page: 0,
                })
              }
            />
            {historyQ.isLoading && !historyQ.data && <Skeleton lines={6} />}
            <ErrorBanner error={historyQ.error} fallbackTitle={t("presetDetail.error.history")} />
            {historyQ.data && (
              <>
                <RunHistoryTable
                  runs={historyQ.data.runs}
                  currentRun={
                    // Only pin the current run on an UNFILTERED first
                    // page: pinning it above a filtered result would
                    // show a row the operator filtered out.
                    search.page === 0 && search.status === "all" && search.mode === "all" && !search.runId
                      ? (historyQ.data.runs.find((r) => r.run_id === currentRunID) ?? null)
                      : null
                  }
                  activeFilterSummary={
                    historyQ.data.runs.length === 0 ? filterSummary(filters, t) : undefined
                  }
                  onClearFilters={() =>
                    setSearch({
                      status: EMPTY_FILTERS.status,
                      mode: EMPTY_FILTERS.mode,
                      runId: undefined,
                      from: undefined,
                      to: undefined,
                      page: 0,
                    })
                  }
                />
                <div className="flex items-center justify-end gap-2">
                  <span className="mono text-2xs t-mute">
                    {t("runState.pageRange", {
                      start: historyQ.data.page_info.total === 0 ? 0 : search.page * HISTORY_PAGE_SIZE + 1,
                      end: Math.min(
                        historyQ.data.page_info.total,
                        (search.page + 1) * HISTORY_PAGE_SIZE,
                      ),
                      total: historyQ.data.page_info.total,
                    })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={search.page === 0}
                    onClick={() => setSearch({ page: Math.max(0, search.page - 1) })}
                  >
                    {t("common.prev")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!historyQ.data.page_info.has_more}
                    onClick={() => setSearch({ page: search.page + 1 })}
                  >
                    {t("common.next")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </SectionCard>
      )}

      {preset && search.tab === "config" && (
        <div className="space-y-3">
          {/* Editing a preset must not make a running or historical run
              look changed — runs carry an immutable config snapshot, and
              this note says so where an operator is about to edit. */}
          <div className="banner" role="status">
            <span aria-hidden>ℹ</span>
            <span>{t("presetDetail.configImmutableNote")}</span>
          </div>
          <PresetEditor
            initial={{ name: preset.name, description: preset.description, config: preset.config }}
            submitLabel={t("preset.save")}
            submitting={saveMut.isPending}
            errorMessage={saveErr}
            onSubmit={(v) => {
              setSaveErr(null);
              // auto_start_enabled is deliberately OMITTED: this form
              // does not own the toggle, and sending `false` would
              // disarm a preset the operator meant to keep armed.
              saveMut.mutate({ name: v.name, description: v.description, config: v.config });
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Overview — a compact facts strip, then the current run (or the
 * latest terminal snapshot).
 *
 * Flat rows rather than nested cards: the addendum asks for a facts
 * strip, and nesting cards inside a card is what made the old preset
 * pages hard to scan.
 */
function OverviewTab({
  preset,
  run,
  runLoading,
  onToggleAutoStart,
  togglingAutoStart,
}: {
  preset: MMPreset;
  run?: MMRun;
  runLoading: boolean;
  onToggleAutoStart: (enabled: boolean) => void;
  togglingAutoStart: boolean;
}) {
  const { t, formatDateTime } = useI18n();
  const mode = effectiveTradingMode(preset.config);
  const pools = preset.config.pools;
  const summary = preset.runs;

  return (
    <div className="space-y-4">
      <Card>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label={t("presets.strategy")} value={strategyNameLabel(preset.config.strategy.name, t)} />
          <Fact label={t("overview.mode")} value={mode === "GAME" ? t("mode.game") : t("mode.cash")} />
          <Fact
            label={t("overview.currencies")}
            value={t("overview.currencyPair", {
              debit: debitCurrency(mode),
              payout: payoutCurrency(mode),
            })}
          />
          <Fact
            label={t("presets.pools")}
            value={t("presets.poolSummary", {
              bid: pools.bid_maker_count,
              ask: pools.ask_maker_count,
              taker: pools.taker_count,
            })}
          />
          <Fact
            label={t("overview.totalRuns")}
            value={summary ? String(summary.total) : "—"}
          />
          <Fact
            label={t("overview.failedRuns")}
            value={summary ? String(summary.failed) : "—"}
          />
          <Fact
            label={t("overview.lastStarted")}
            value={summary?.last_started_at ? formatDateTime(summary.last_started_at) : "—"}
          />
          <Fact
            label={t("common.updated")}
            value={t("overview.updatedBy", {
              time: formatDateTime(preset.updated_at),
              who: preset.updated_by || "—",
            })}
          />
        </dl>
      </Card>

      {/* Auto-start. Labelled by what it DOES, never "default" — the
          old word answered a question that only had one answer, and
          this deployment can arm several presets. */}
      <SectionCard title={t("autoStart.title")}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-t-1">{t("autoStart.toggleLabel")}</p>
            <p className="mt-1 text-xs t-mute">{t("autoStart.toggleHelp")}</p>
          </div>
          <Button
            variant={preset.auto_start_enabled ? "ghost" : "primary"}
            onClick={() => onToggleAutoStart(!preset.auto_start_enabled)}
            loading={togglingAutoStart}
          >
            {preset.auto_start_enabled ? t("autoStart.disable") : t("autoStart.enable")}
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        title={
          run?.is_active ? t("presetDetail.currentRun") : t("presetDetail.latestRun")
        }
      >
        {runLoading && <Skeleton lines={3} />}
        {!run && !runLoading && <p className="t-mute text-sm">{t("presetDetail.noRunsYet")}</p>}
        {run && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={run.status} />
              <ModeBadge mode={run.trading_mode} />
              <span className="text-sm text-t-1">{run.preset_name}</span>
              <NameSourceBadge source={run.preset_name_source} />
              {(run.recovery?.attempt ?? 1) > 1 && (
                <Badge tone="warn">
                  {t("history.attemptOf", {
                    attempt: run.recovery.attempt,
                    source: run.recovery.resumed_from_run_id?.slice(0, 8) ?? "—",
                  })}
                </Badge>
              )}
            </div>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Fact
                label={t("overview.started")}
                value={run.start_at ? formatDateTime(run.start_at) : formatDateTime(run.start_requested_at)}
              />
              <Fact
                label={t("overview.stopped")}
                value={run.stop_at ? formatDateTime(run.stop_at) : "—"}
              />
              <Fact
                label={t("presetDetail.checkpointFreshness")}
                value={
                  run.checkpoint?.available && run.checkpoint.captured_at
                    ? formatDateTime(run.checkpoint.captured_at)
                    : t("presetDetail.noCheckpoint")
                }
              />
              <Fact
                label={t("history.col.snapshot")}
                value={
                  run.snapshot
                    ? run.snapshot.complete
                      ? t("history.snapshotCaptured")
                      : t("runState.completeness.partial")
                    : t("runState.completeness.notCaptured")
                }
              />
            </dl>
            <ErrorBanner error={run.last_error} fallbackTitle={t("run.error.reported")} />
            <Link
              to="/runs/$id"
              params={{ id: run.run_id }}
              className="inline-block text-xs t-emph hover:underline"
            >
              {t("presetDetail.openRun")}
            </Link>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-2xs uppercase tracking-widest text-t-3">{label}</dt>
      <dd className="mt-1 truncate text-sm text-t-1" title={value}>
        {value}
      </dd>
    </div>
  );
}
